import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

/*
 * Status badges pair a colour with a LABEL, always. A status colour never
 * carries meaning on its own — that is the mitigation for warning/serious
 * sitting under 3:1 on the light surface.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "border-transparent bg-secondary text-secondary-foreground",
        outline: "border-border text-foreground",
        good: "border-transparent bg-[color-mix(in_srgb,var(--status-good)_16%,transparent)] text-[color-mix(in_srgb,var(--status-good)_82%,black)] dark:text-[var(--status-good)]",
        warning:
          "border-transparent bg-[color-mix(in_srgb,var(--status-warning)_20%,transparent)] text-[color-mix(in_srgb,var(--status-warning)_65%,black)] dark:text-[var(--status-warning)]",
        serious:
          "border-transparent bg-[color-mix(in_srgb,var(--status-serious)_20%,transparent)] text-[color-mix(in_srgb,var(--status-serious)_70%,black)] dark:text-[var(--status-serious)]",
        critical:
          "border-transparent bg-[color-mix(in_srgb,var(--status-critical)_16%,transparent)] text-[color-mix(in_srgb,var(--status-critical)_88%,black)] dark:text-[var(--status-critical)]",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/** Maps backend enum values onto a status tone. */
export function toneFor(value: string | null | undefined): BadgeTone {
  switch (value) {
    case "VERIFIED":
    case "APPROVED":
    case "ACTIVE":
    case "RESOLVED":
    case "COMPLETED":
    case "PAID":
      return "good";
    case "PENDING":
    case "PENDING_REVIEW":
    case "PENDING_CUSTOMER":
    case "IN_PROGRESS":
    case "INVITED":
    case "DELETION_PENDING":
      return "warning";
    case "HIGH":
    case "OPEN":
      return "serious";
    case "REJECTED":
    case "DISABLED":
    case "URGENT":
    case "FAILED":
      return "critical";
    default:
      return "default";
  }
}

export { Badge, badgeVariants };
